import { useEffect, useMemo, useCallback } from "react";
import { useIntl } from "react-intl";
import { TabListView } from "@/components/molecules/tabListView";
import {
	ToggleItemData,
	ToggleListView,
} from "@/components/molecules/toggleListView";
import {
	Apps20Regular,
	BookContacts20Regular,
	ChartMultipleRegular,
	Compose20Regular,
} from "@fluentui/react-icons";
import { AgentActions, AgentState, useAgentStore } from "@/common/stores/persistingStores/agentPermissions";
import {
	NavigationActions,
	NavigationState,
	TabType,
	ToggleTabType,
	useNavigationStore,
} from "@/common/stores/persistingStores/navigation";
import { PopoverView } from "@/components/atoms/popover";
import { useStyle } from "./style";
import { AccountDetails } from "@/components/organisms/account";
import { AvatarView } from "@/components/atoms/avatar/AvatarView";
import {
	getUserShortProfile,
	presencesQueryKey,
	userShorProfileQueryKey,
	agentTotalWorkloadQueryKey,
	getAgentTotalWorkload,
	agentWorkloadQueryKey,
	verifyHasClient,
} from "@/repository/queryFns";
import { notificationListener } from "@/hooks/notification";
import {
	AgentWorkload,
	AgentWorkloadNotification,
	QueueChanged,
	UserPresenceChanged,
	UserShortProfileDto,
} from "@/repository/models";
import { ServerEvent } from "@/common/notification";
import { StorageKeys } from "@/common/constants/storageKeys";
import { Lockr } from "@/common/Lockr";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QueuesState, useQueuesStore } from "@/common/stores/nonpersistingStores/queues";
import {
	SupervisorState,
	useSupervisorStore,
} from "@/common/stores/nonpersistingStores/supervisor";
import PlaySoundHelper, { SoundType } from "@/common/helpers/play-sound-helper";
import {
	getRingingFilePath,
	ringingFilePathQueryKey,
} from "@/repository/queryFns/utils";
import { useAuth } from "@/hooks/authentication";
import { Availabilities } from "@/components/organisms/currentUserPresence/types";
import { useMessage } from "@/common/messages/types";
import {
	interactionsQueryKey,
	supervisorActiveInteractionKey,
} from "@/repository/queryFns/interactions";
import {
	useCurrentInteractionStore,
	CurrentInteractionActions,
	NewInteraction,
	CurrentInteractionState
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { AppCacheActions, CacheKey, useAppCacheStore } from "@/common/stores/persistingStores/app";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import { useAllowedOutbound } from "@/hooks/outbound";
import { InteractionType } from "@/common/types";
import { addBreadcrumb } from "@/common/helpers/sentryHelper";
import { useTabItems } from "@/hooks/navigationTabs";
import { CancelSupervisorInvite, CancelSupervisorReason } from "@/common/models/cancelSupervisorInvite";
import { shouldCancelActiveInteractionForWrapup } from "./shouldCancelActiveInteractionForWrapup";

export const NavigationPanel = () => {
	const intl = useIntl();
	const classes = useStyle();
	const authContext = useAuth();

	const currentInteractionActions = useCurrentInteractionStore(
		(actions: CurrentInteractionActions) => actions
	);

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);

	const isJoinedOnAnyQueue = useQueuesStore(
		(state: QueuesState) => state.isJoinedOnAnyQueue
	);

	const appSettings = useAppSettingsStore((state) => state.settings);

	const allowedOutbound = useAllowedOutbound();

	const { show: showMessage } = useMessage();
	const currentAgentState = useAgentStore((state: AgentState) => state);
	const currentAgentActions = useAgentStore((actions: AgentActions) => actions);

	const activeInteractionsCount = useNavigationStore(
		(state: NavigationState) => state.activeInteractionsCount
	);
	const selectedTab = useNavigationStore(
		(state: NavigationState) => state.selectedTab
	);
	const toggledTab = useNavigationStore(
		(state: NavigationState) => state.toggledTab
	);
	const setSelectedTab = useNavigationStore(
		(state: NavigationActions) => state.setSelectedTab
	);
	const setToggledTab = useNavigationStore(
		(state: NavigationActions) => state.setToggledTab
	);
	const isQueueSupervisor = useSupervisorStore(
		(state: SupervisorState) => state.isSupervisor
	);

	const queues = useQueuesStore((state: QueuesState) => state.queues);
	const queuesDataLoaded = useQueuesStore(
		(state: QueuesState) => state.isDataLoaded
	);

	const agentCanViewAllInteractions = useMemo<boolean>(
		() =>
			queues.filter((x) => {
				return (
					x.isSupervisor || (x.isOperator && x.hasAllInteractionVisibilityRight)
				);
			}).length > 0,
		[queues]
	);

	const agentHasOperatorAgentViewPermission = useMemo<boolean>(
		() =>
			queues.filter((x) => {
				return x.isOperator && x.hasAgentsActivityRight;
			}).length > 0,
		[queues]
	);

	const queryClient = useQueryClient();

	const tabItemsData = useTabItems({
		activeInteractionsCount,
		agentCanViewAllInteractions,
		isQueueSupervisor,
		agentHasOperatorAgentViewPermission,
	});

	const toggleItemsData = useMemo<ToggleItemData<ToggleTabType>[]>(() => {
		return [
			{
				key: ToggleTabType.NewInteraction,
				icon: <Compose20Regular />,
				text: intl.formatMessage({ id: "InteractionMenu.New" }),
				disabled: allowedOutbound.possibleOutboundTypes.length === 0,
				onClick: () => {
					if (allowedOutbound.possibleOutboundTypes[0] !== undefined) {
						onNewInteractionCallback(allowedOutbound.possibleOutboundTypes[0]);
					}
				},
			},
			{ key: ToggleTabType.Contacts, icon: <BookContacts20Regular /> },
			{ key: ToggleTabType.Resources, icon: <Apps20Regular /> },
			...(appSettings.queueStatisticsEnabled
				? [{ key: ToggleTabType.Statistics, icon: <ChartMultipleRegular /> }]
				: []),
		];
	}, [
		allowedOutbound.possibleOutboundTypes,
		appSettings.queueStatisticsEnabled,
	]);

	const { data: userShortProfile } = useQuery<UserShortProfileDto>({
		queryKey: [userShorProfileQueryKey],
		queryFn: getUserShortProfile,
	});

	useQuery<number>({
		queryKey: [agentTotalWorkloadQueryKey],
		queryFn: getAgentTotalWorkload,
	});

	useQuery<string>({
		queryKey: [ringingFilePathQueryKey, true],
		queryFn: () => getRingingFilePath(true),
	});

	const userId = Lockr.get(StorageKeys.UserId) as string;
	const avatarView = useMemo(
		() => (
			<AvatarView
				agentState={{
					fullName: currentAgentState.fullName,
					availability: currentAgentState.availability,
					activity: currentAgentState.activity,
				}}
				photoId={userId}
				className={!isJoinedOnAnyQueue ? classes.isNotJoinedBorder : ""}
				size={36}
			/>
		),
		[currentAgentState, isJoinedOnAnyQueue, userId]
	);

	useEffect(() => {
		if (userShortProfile) {
			currentAgentActions.setJob(
				userShortProfile.jobTitle,
				userShortProfile.department
			);
			currentAgentActions.setName(userShortProfile.fullName);
		}
	}, [userShortProfile]);

	useEffect(() => {
		if (isQueueSupervisor == undefined || !queuesDataLoaded) {
			return;
		}

		if (
			isQueueSupervisor === false &&
			!agentHasOperatorAgentViewPermission &&
			(selectedTab === TabType.Supervisor ||
				(selectedTab === TabType.SupervisorInteractions &&
					!agentCanViewAllInteractions))
		) {
			setSelectedTab(TabType.Interactions);
		}
	}, [
		isQueueSupervisor,
		agentCanViewAllInteractions,
		queuesDataLoaded,
		agentHasOperatorAgentViewPermission,
	]);

	notificationListener(ServerEvent.PlayBusyTone, (data: any) => {
		PlaySoundHelper.playSound(SoundType.Busy, data.busyTonePath);
	});

	notificationListener<UserPresenceChanged>(
		ServerEvent.UserPresenceChanged,
		(data: UserPresenceChanged) => {
			if (data.userId == Lockr.get(StorageKeys.UserId)) {
				if (
					authContext.isLogged &&
					currentAgentState.availability === Availabilities.Offline &&
					data.availability !== Availabilities.Offline
				) {
					verifyHasClient();
				}

				currentAgentActions.setPresence(data.availability, data.activity);

				if (data.clearCallInfo) {
					cancelActiveInteractionIfNeeded(data.wrapupMediaId);
				}
			}
		}
	);

	const cancelActiveInteractionIfNeeded = (wrapupMediaId: string | undefined) => {
		if (!shouldCancelActiveInteractionForWrapup(wrapupMediaId, currentInteraction?.id)) {
			return;
		}

		currentInteractionActions.hideActive();
		queryClient.invalidateQueries({
			queryKey: [supervisorActiveInteractionKey],
		});
		queryClient.invalidateQueries({ queryKey: [interactionsQueryKey] });
	};

	notificationListener<any>(
		ServerEvent.AgentAnsweredAfterOfferingTimeout,
		(data: any) => {
			if (data.userId == Lockr.get(StorageKeys.UserId)) {
				showMessage("workspace", {
					intent: "error",
					text: intl.formatMessage({
						id: "ErrorMessage.AgentAnswerAfterOfferingTimeout",
					}),
				});
			}
		}
	);

	notificationListener<any>(
		ServerEvent.CancelSupervisorInvite,
		(data: CancelSupervisorInvite) => {
			if (data.userId == Lockr.get(StorageKeys.UserId)) {
				const message = getCancelSupervisorOfferingMessage(data.reason);
				showMessage("global", {
					intent: "error",
					text: message
				});
			}
		}
	);

	notificationListener<any>(
		ServerEvent.CalleeDeclinedInvite,
		() => {
			showMessage("workspace", {
				intent: "error",
				text: intl.formatMessage({
					id: "ErrorMessage.CalleeDeclined",
				}),
			});
		}
	);

	notificationListener<QueueChanged>(
		ServerEvent.QueueChanged,
		(queue: QueueChanged) => {
			const oneOfCurrentUsersQueuesChanged = !!queues.find(
				(x) => x.id == queue.id
			);

			if (oneOfCurrentUsersQueuesChanged) {
				queryClient.invalidateQueries({
					queryKey: [presencesQueryKey],
					type: "all",
					refetchType: "all",
				});
			}
		}
	);

	notificationListener<AgentWorkloadNotification>(
		ServerEvent.AgentWorkloadChanged,
		(agentUtilization) => {
			queryClient.setQueryData(
				[agentTotalWorkloadQueryKey],
				(_: number) => agentUtilization.totalWorkload ?? 0
			);

			if (agentUtilization.totalWorkload) {
				queryClient.invalidateQueries({
					queryKey: [agentWorkloadQueryKey, agentUtilization.agentId],
				});
			} else {
				queryClient.setQueryData(
					[agentWorkloadQueryKey, agentUtilization.agentId],
					(oldData: AgentWorkload) => ({
						...oldData,
						totalWorkload: 0,
						workloadInteractionGroups: [],
					})
				);
			}

			if (agentUtilization.triggeredBySupervisorRefresh) {
				showMessage("global", {
					intent: "info",
					text: intl.formatMessage({
						id: "Account.RefreshedPerformedBySupervisor",
					}),
				});

				queryClient.invalidateQueries({ queryKey: [interactionsQueryKey] });
			}
		}
	);

	const onNewInteractionCallback = useCallback(
		(type: InteractionType) => {
			addBreadcrumb("ui_interaction", "New interaction clicked", { type });

			setToggledTab(ToggleTabType.NewInteraction);
			const newInteraction: NewInteraction = {
				type: type,
				data: undefined,
			};

			appStoreActions.setCache(CacheKey.OutboundType, type);

			currentInteractionActions.showNew(newInteraction);
		},
		[currentInteractionActions.showNew, appStoreActions, setToggledTab]
	);

	const getCancelSupervisorOfferingMessage = (reason: CancelSupervisorReason) => {
		switch (reason) {
			case CancelSupervisorReason.ColdTransfer:
				return intl.formatMessage({
					id: "ErrorMessage.CancelSupervisorReason.ColdTransfer"
				});
			case CancelSupervisorReason.WarmTransfer:
				return intl.formatMessage({
					id: "ErrorMessage.CancelSupervisorReason.WarmTransfer"
				});
			case CancelSupervisorReason.Park:
				return intl.formatMessage({
					id: "ErrorMessage.CancelSupervisorReason.Park"
				});
			case CancelSupervisorReason.WarmTransferIvr:
				return intl.formatMessage({
					id: "ErrorMessage.CancelSupervisorReason.WarmTransferIvr"
				});
			case CancelSupervisorReason.ColdTransferIvr:
				return intl.formatMessage({
					id: "ErrorMessage.CancelSupervisorReason.ColdTransferIvr"
				});
			default:
				return intl.formatMessage({
					id: "ErrorMessage.CancelSupervisorReason.Default"
				});
		}
	}

	return (
		<div className={classes.container}>
			<TabListView
				tabs={tabItemsData}
				selected={selectedTab}
				onTabSelect={setSelectedTab}
			/>
			<div className={classes.rightContainer}>
				<ToggleListView
					selected={toggledTab}
					items={toggleItemsData}
					onToggle={(tab?: ToggleTabType) => setToggledTab(tab)}
				/>
				<PopoverView
					classes={{ content: classes.accountPopover }}
					positioning={{
						overflowBoundaryPadding: 16,
						position: "below",
						align: "start",
					}}
					trigger={avatarView}
					content={<AccountDetails />}
				/>
			</div>
		</div>
	);
};
